import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { zodParsingCaseActionResponseSchema } from '../validations';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  404: () =>
    i18n.t(($) => $.retryProcessParsingCase.error404, {
      ns: 'data',
    }),
  409: () =>
    i18n.t(($) => $.retryProcessParsingCase.error409, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.retryProcessParsingCase.error422, {
      ns: 'data',
    }),
};

/**
 * Retry the processing step of a parsing case from its latest PARSED_JSON artifact.
 * Skips OCR and parsing; intended for cases where an external fix (party matching,
 * site/accounting reference mapping) has been applied. Allowed regardless of case status.
 *
 * @param caseId - UUID of the parsing case
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_process_vadmin_parsing_cases__case_id__actions_retry_process_post
 */
export const retryProcessParsingCase = async (
  caseId: UUID,
): Promise<ParsingCaseActionMutationResponse> => {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.RETRY_PROCESS(caseId), {
        validate: { response: zodParsingCaseActionResponseSchema },
      })
      .json<ParsingCaseActionMutationResponse>();
  } catch (error) {
    log.error({
      message: 'Error retrying process for parsing case',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.retryProcessParsingCase.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
